import { Link } from "react-router-dom";
import { ChevronRight, Layers, Loader2, X } from "lucide-react";

import { CarrierLogo } from "@/components/common/CarrierLogo";
import {
  SensitiveMoney,
  SensitiveText,
} from "@/components/common/Sensitive";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useRightRail } from "@/context/RightRailContext";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { formatRouteLabel } from "@/lib/formatPort";
import { resolvePartyNameDisplay } from "@/lib/bookingPartyDisplay";
import { cn } from "@/lib/utils";
import { CollapsibleRailSection } from "@/shell/CollapsibleRailSection";
import {
  railSectionStackClassName,
  railStackInlineScrollBodyClassName,
  railStackInlineScrollRootClassName,
  railStackSectionContentClassName,
  useRailStackPanel,
} from "@/shell/RailStackContext";
import type { BlGroup, BlGroupBooking } from "@/types/api";

import {
  blGroupCountLabel,
  formatBlCharge,
  useBookingBlGroup,
} from "./bookingBlGroup";
import { BookingStateBadge } from "./BookingStateBadge";

interface BookingBlGroupRailProps {
  sourceId: string;
  onClose: () => void;
}

function Charge({
  amount,
  currency,
  className,
}: {
  amount?: number | null;
  currency?: string | null;
  className?: string;
}) {
  const formatted = formatBlCharge(amount, currency);
  if (!formatted) return <span className={className}>—</span>;
  return <SensitiveMoney formatted={formatted} className={className} />;
}

function BlGroupSummary({ group }: { group: BlGroup }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="rounded-lg bg-muted/40 px-2.5 py-2">
        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Total BL charge
        </p>
        <p className="mt-0.5 text-sm font-semibold tabular-nums">
          <Charge amount={group.bl_fee} currency={group.currency} />
        </p>
      </div>
      <div className="rounded-lg bg-muted/40 px-2.5 py-2">
        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Per booking
        </p>
        <p className="mt-0.5 text-sm font-semibold tabular-nums">
          <Charge
            amount={group.bl_charge_per_booking}
            currency={group.currency}
          />
        </p>
      </div>
    </div>
  );
}

function SiblingRow({
  item,
  currency,
}: {
  item: BlGroupBooking;
  currency?: string | null;
}) {
  const consignee = resolvePartyNameDisplay(
    item.consignee_name ?? undefined,
    item.consignee ?? undefined,
  );
  const href = `/bookings/${encodeURIComponent(item.source_id)}`;
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 truncate text-sm font-medium">{item.source_id}</p>
        <p className="shrink-0 text-sm font-medium tabular-nums">
          <Charge
            amount={item.bl_charge}
            currency={item.pricing_currency ?? currency}
          />
        </p>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        <BookingStateBadge state={item.state} />
        {item.is_current && (
          <Badge variant="info" className="h-5 px-1.5 text-[10px]">
            This booking
          </Badge>
        )}
      </div>
      {item.equipment_summary && (
        <p className="mt-1 truncate text-xs text-muted-foreground">
          {item.equipment_summary}
        </p>
      )}
      {item.carrier_bl_number?.trim() && (
        <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
          B/L {item.carrier_bl_number.trim()}
        </p>
      )}
      {consignee && (
        <p className="truncate text-xs text-muted-foreground">
          <SensitiveText value={consignee} />
        </p>
      )}
    </>
  );

  if (item.is_current) {
    return (
      <div className="rounded-lg border border-primary/25 bg-primary/5 px-2.5 py-2">
        {body}
      </div>
    );
  }

  return (
    <Link
      to={href}
      className="block rounded-lg border border-border/60 px-2.5 py-2 transition-colors hover:bg-accent/60"
    >
      {body}
    </Link>
  );
}

function BookingBlGroupCard({
  sourceId,
  group,
  loading,
  error,
  onClose,
  showClose = true,
  stackGrow = false,
}: {
  sourceId: string;
  group: BlGroup | null;
  loading: boolean;
  error: string | null;
  onClose?: () => void;
  showClose?: boolean;
  stackGrow?: boolean;
}) {
  const { preferences } = useUserPreferences();
  const { formatDate } = useDateFormatter();
  const route = group
    ? formatRouteLabel(
        group.origin_port,
        group.destination_port,
        preferences.showLocodes,
      )
    : null;

  const listBody = (
    <>
      {loading && (
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading BL group…
        </div>
      )}

      {!loading && error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {!loading && !error && !group && (
        <p className="text-sm text-muted-foreground">
          This booking is not part of a BL group.
        </p>
      )}

      {group && (
        <>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {group.carrier && <CarrierLogo carrier={group.carrier} size={16} />}
            {route && <span className="truncate">{route}</span>}
            {group.departure_date && (
              <span>{formatDate(group.departure_date)}</span>
            )}
            {group.bl_number?.trim() && (
              <span className="font-mono">B/L {group.bl_number.trim()}</span>
            )}
          </div>
          <BlGroupSummary group={group} />
          <ul className="flex flex-col gap-2">
            {group.bookings.map((item) => (
              <li key={item.source_id}>
                <SiblingRow
                  item={item}
                  currency={item.pricing_currency ?? group.currency}
                />
              </li>
            ))}
          </ul>
          {group.bookings.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No sibling bookings found for {sourceId}.
            </p>
          )}
        </>
      )}
    </>
  );

  return (
    <div
      className={railStackInlineScrollRootClassName(
        stackGrow,
        "flex flex-col gap-3",
      )}
    >
      <div className="flex shrink-0 items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">
            Bookings sharing one bill of lading
          </p>
          {group && (
            <p className="truncate text-sm font-medium">
              {blGroupCountLabel(group.booking_count)}
            </p>
          )}
        </div>
        {showClose && onClose && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            aria-label="Close BL group"
            onClick={onClose}
          >
            <X className="size-3.5" />
          </Button>
        )}
      </div>

      {stackGrow ? (
        <div
          className={railStackInlineScrollBodyClassName(
            true,
            "flex flex-col gap-3 pr-0.5",
          )}
        >
          {listBody}
        </div>
      ) : (
        listBody
      )}
    </div>
  );
}

/** Desktop stack card for a booking's BL-group siblings. */
export function BookingBlGroupRailSection({
  sourceId,
  stack = false,
  stackAnimate = false,
  stackGrow = false,
}: {
  sourceId: string;
  stack?: boolean;
  stackAnimate?: boolean;
  stackGrow?: boolean;
}) {
  const { toggleBookingBlGroupSection, setBookingBlGroupRail } = useRightRail();
  const view = useRailStackPanel("blGroup");
  const { group, loading, error } = useBookingBlGroup(sourceId);

  return (
    <CollapsibleRailSection
      title="BL group"
      icon={Layers}
      badge={group && group.booking_count > 0 ? group.booking_count : undefined}
      expanded={view.expanded}
      accordion={view.accordion}
      fill={view.fill}
      flow={view.flow}
      scroll={stackGrow ? false : view.scroll}
      onToggle={toggleBookingBlGroupSection}
      className={railSectionStackClassName(view, {
        mobileStack: stack,
        stackAnimate,
        stackGrow,
      })}
      contentClassName={stack ? undefined : "pr-0.5"}
    >
      <div className={railStackSectionContentClassName(stackGrow)}>
        <BookingBlGroupCard
          sourceId={sourceId}
          group={group}
          loading={loading}
          error={error}
          onClose={() => setBookingBlGroupRail(null)}
          showClose
          stackGrow={stackGrow}
        />
      </div>
    </CollapsibleRailSection>
  );
}

/** Mobile bottom sheet for a booking's BL-group siblings. */
export function BookingBlGroupRail({
  sourceId,
  onClose,
}: BookingBlGroupRailProps) {
  const { group, loading, error } = useBookingBlGroup(sourceId);

  const body = (
    <BookingBlGroupCard
      sourceId={sourceId}
      group={group}
      loading={loading}
      error={error}
      onClose={onClose}
      showClose
    />
  );

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="bottom"
        className="mx-auto max-h-[min(90dvh,var(--app-vvh,90vh))] max-w-lg rounded-t-2xl pb-safe"
      >
        <SheetHeader className="mb-3 shrink-0 pr-8">
          <SheetTitle>BL group</SheetTitle>
          <SheetDescription>
            {group
              ? `${blGroupCountLabel(group.booking_count)} sharing one bill of lading`
              : `Bookings sharing a bill of lading with ${sourceId}`}
          </SheetDescription>
        </SheetHeader>
        <div className="sheet-scroll flex-1">{body}</div>
      </SheetContent>
    </Sheet>
  );
}

interface BlGroupDetailRowProps {
  sourceId: string;
  groupId?: string | null;
  blCharge?: number | null;
  currency?: string | null;
  /** MV carrier B/L for this booking (shown when the group payload has no shared number yet). */
  carrierBlNumber?: string | null;
  open: boolean;
  onToggle: () => void;
}

/** Commercial-details control: count + charges, opens the BL group rail/sheet. */
export function BlGroupDetailRow({
  sourceId,
  groupId,
  blCharge,
  currency,
  carrierBlNumber,
  open,
  onToggle,
}: BlGroupDetailRowProps) {
  const { group, loading } = useBookingBlGroup(groupId ? sourceId : null);

  if (!groupId) {
    return (
      <div className="flex items-start justify-between gap-4 py-2">
        <dt className="text-sm text-muted-foreground">BL group</dt>
        <dd className="text-right text-sm font-medium">—</dd>
      </div>
    );
  }

  const count = group ? blGroupCountLabel(group.booking_count) : null;
  const blNumber =
    group?.bl_number?.trim() || carrierBlNumber?.trim() || undefined;
  const total = group
    ? formatBlCharge(group.bl_fee, group.currency)
    : undefined;
  const perBooking = formatBlCharge(
    group?.bl_charge_per_booking ?? blCharge,
    group?.currency ?? currency,
  );

  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <dt className="text-sm text-muted-foreground">BL group</dt>
      <dd className="min-w-0 text-right">
        <button
          type="button"
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-busy={loading || undefined}
          onClick={onToggle}
          className={cn(
            "inline-flex max-w-full flex-col items-end gap-0.5 rounded-lg px-1.5 py-0.5 text-right transition-colors",
            "hover:bg-accent",
            open && "bg-accent",
          )}
        >
          <span className="inline-flex items-center gap-1 text-sm font-medium">
            {count ?? (loading ? "BL group" : "View group")}
            {loading ? (
              <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
            ) : (
              <ChevronRight
                className={cn(
                  "size-3.5 shrink-0 text-muted-foreground transition-transform",
                  open && "rotate-90",
                )}
              />
            )}
          </span>
          {blNumber && (
            <span className="font-mono text-xs text-muted-foreground">
              B/L {blNumber}
            </span>
          )}
          {(total || perBooking) && (
            <span className="text-xs text-muted-foreground">
              {total && <SensitiveMoney formatted={total} />}
              {total && perBooking && " total · "}
              {perBooking && (
                <>
                  <SensitiveMoney formatted={perBooking} />
                  {" / booking"}
                </>
              )}
            </span>
          )}
        </button>
      </dd>
    </div>
  );
}
